import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, BookOpen } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import { isStudentLoggedIn, studentApi, setStudentToken } from "../lib/studentAuth";

const links = [
    { to: "/", label: "Home" },
    { to: "/story", label: "The Story" },
    { to: "/courses", label: "Courses" },
    { to: "/journal", label: "Journal" },
    { to: "/quiz", label: "Assessment" },
    { to: "/free-tools", label: "Free Tools" },
    { to: "/companion", label: "Companion" },
    { to: "/speaking", label: "Speaking" },
    { to: "/help", label: "Get Help" },
];

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [student, setStudent] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isStudentLoggedIn()) return;
        studentApi.get("/students/me")
            .then((r) => setStudent(r.data))
            .catch(() => {
                // Token invalid or expired — clear it silently
                setStudentToken(null);
                setStudent(null);
            });
    }, []);

    const myCoursesHref = (() => {
        const ids = student?.course_ids || [];
        if (ids.includes("battlefield-course")) return "/course/dashboard";
        if (ids.includes("daily-battle-drill")) return "/daily-battle-drill";
        return "/courses";
    })();

    const signOut = () => {
        setStudentToken(null);
        setStudent(null);
        toast.success("Signed out.");
        navigate("/");
    };

    return (
        <header
            className="sticky top-0 z-40 bg-black/85 backdrop-blur border-b border-white/10"
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                    <Logo size={36} />
                    <div className="leading-none">
                        <div
                            className="font-display text-2xl tracking-wider"
                            style={{ letterSpacing: "0.18em" }}
                        >
                            MINDWORKS
                        </div>
                        <div className="text-[10px] tracking-[0.3em] text-red-600 mt-0.5">
                            THINK. FIGHT. TRANSFORM.
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `px-4 py-2 text-sm font-display tracking-[0.18em] uppercase transition-colors ${
                                    isActive
                                        ? "text-red-600"
                                        : "text-white/80 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    {student ? (
                        <>
                            <Link
                                to={myCoursesHref}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-display tracking-[0.18em] uppercase text-white/85 hover:text-red-600 transition-colors"
                                data-testid="nav-my-courses"
                            >
                                <BookOpen size={14} /> My Courses
                            </Link>
                            <button
                                onClick={signOut}
                                className="text-white/45 hover:text-red-600 transition-colors p-2"
                                aria-label="Sign out"
                                data-testid="nav-signout"
                                title="Sign out"
                            >
                                <LogOut size={16} />
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/companion"
                            className="inline-flex btn-primary text-sm"
                            data-testid="cta-companion-nav"
                        >
                            Battle Now
                        </Link>
                    )}
                </div>

                <button
                    className="lg:hidden text-white p-2"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/10 bg-black" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `px-2 py-3 font-display tracking-[0.18em] uppercase ${
                                        isActive ? "text-red-600" : "text-white/90"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {student && (
                            <>
                                <Link
                                    to={myCoursesHref}
                                    onClick={() => setOpen(false)}
                                    className="px-2 py-3 font-display tracking-[0.18em] uppercase text-white/90 flex items-center gap-2"
                                    data-testid="nav-my-courses-mobile"
                                >
                                    <BookOpen size={14} /> My Courses
                                </Link>
                                <button
                                    onClick={() => { setOpen(false); signOut(); }}
                                    className="px-2 py-3 font-display tracking-[0.18em] uppercase text-white/55 text-left flex items-center gap-2"
                                    data-testid="nav-signout-mobile"
                                >
                                    <LogOut size={14} /> Sign out
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
